import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { StoryCard } from "../../Molecules/StoryCard";
import { collectionToStories } from "@quintype/components";
import { CollectionName } from "../../Atoms/CollectionName";
import { Headline } from "../../Atoms/Headline";
import { AuthorWithTime } from "../../Atoms/AuthorWithTimestamp";
import { HeroImage } from "../../Atoms/HeroImage";
import { generateNavigateSlug, getTextColor, navigateTo } from "../../../utils/utils";
import { StateProvider } from "../../SharedContext";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { useDispatch, useSelector } from "react-redux";
import { pushThreeColTwelveStoriesDataLayer } from "./dataLayerUtils";
import { useTrackWidgetImpression } from "../../../../ga-utils";

import "./three-col-twelve-stories.m.css";

const getChildCollectionData = (collection = {}, config = {}, collectionIndex, qtConfig, pageType) => {
  const stories = collectionToStories(collection);
  if (!stories.length) return null;
  const [firstStory, ...otherStories] = stories;
  const {
    collectionNameTemplate,
    withSeparator = true,
    theme,
    footerButton = "",
    collectionNameBorderColor = "",
  } = config;

  const dispatch = useDispatch();
  const url = generateNavigateSlug(collection, qtConfig);

  const sectionName = collection.name || collection.slug || "";
  const handleSectionNameClick = () => {
    pushThreeColTwelveStoriesDataLayer({
      link_position: -2,
      section_name: sectionName,
      pageType,
      windowLocation: window.location,
    });
    navigateTo(dispatch, url);
  };
  const handleReadMoreClick = () => {
    pushThreeColTwelveStoriesDataLayer({
      link_position: -1,
      section_name: sectionName,
      pageType,
      windowLocation: window.location,
    });
    navigateTo(dispatch, url);
  };
  return (
    <div styleName="column" style={{ order: collectionIndex }}>
      <div styleName="collection-name">
        <div onClick={handleSectionNameClick} style={{ cursor: "pointer" }}>
          <CollectionName
            collection={collection}
            collectionNameTemplate={collectionNameTemplate}
            collectionNameBorderColor={collectionNameBorderColor}
          />
        </div>
        <div onClick={handleReadMoreClick} style={{ cursor: "pointer" }}>
          <LoadmoreButton
            config={{ ...config, buttonText: null }}
            collection={collection}
            qtConfig={qtConfig}
            isBtnWithArrow
          />
        </div>
      </div>
      <StoryCard story={firstStory} theme={theme} border={withSeparator ? "bottom" : ""} config={config}>
        <div
          styleName="big-card"
          onClick={() => {
            pushThreeColTwelveStoriesDataLayer({
              link_position: 1,
              section_name: sectionName,
              pageType,
              windowLocation: window.location,
            });
          }}
        >
          <div styleName="big-card-hero-image">
            <HeroImage story={firstStory} aspectRatio={[[16, 9]]} />
          </div>
          <div styleName="big-card-content">
            <Headline story={firstStory} premiumStoryIconConfig={config} />
            <AuthorWithTime config={config} story={firstStory} hideAuthorImage={true} showAuthorContent={false} />
          </div>
        </div>
      </StoryCard>
      {otherStories.slice(0, 2).map((story, index) => (
        <div
          styleName={`small-card ${!index ? "border" : ""}`}
          key={story.id}
          onClick={() => {
            pushThreeColTwelveStoriesDataLayer({
              link_position: index + 2, // firstStory is position 1, these are 2, 3
              section_name: sectionName,
              pageType,
              windowLocation: window.location,
            });
          }}
        >
          <StoryCard story={story} theme={theme} config={config}>
            <div styleName="small-card-top-row">
              <div styleName="small-card-content">
                <Headline story={story} premiumStoryIconConfig={config} />
              </div>
            </div>
            <AuthorWithTime config={config} story={story} hideAuthorImage={true} showAuthorContent={false} />
          </StoryCard>
        </div>
      ))}
      <LoadmoreButton
        template={footerButton}
        collection={collection}
        config={config}
        navigate={() => navigateTo(dispatch, url)}
        qtConfig={qtConfig}
      />
    </div>
  );
};

function ThreeColTwelveStories({ collection, config = {} }) {
  const childCollections = get(collection, ["items"], []).filter((collections) => collections.type === "collection");
  if (childCollections.length < 2) return null; // if number of collection is less than 2 return null
  const { theme = "", slotConfig = [] } = config;
  const isSlotTypeStory = get(slotConfig, [0, "type"], "story") === "story";
  const collectionCount = isSlotTypeStory ? 3 : 2;
  const textColor = getTextColor(theme);
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const adWidgetComponent = get(slotConfig, [0, "component"]);
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], {}));

  const isBrowser = typeof window !== "undefined";

  // Use the custom hook for tracking widget impressions
  const componentRef = useTrackWidgetImpression(
    {
      sectionName: collection?.name,
      pageType: pageType,
      location: isBrowser ? window.location : null,
      config: qtConfig,
      threshold: 0.2,
    },
    { disabled: !isBrowser }
  );
  return (
    <div
      className="full-width-with-padding arrow-component arr--three-col-twelve-stories"
      data-test-id="three-col-twelve-stories"
      style={{ backgroundColor: theme, color: textColor }}
      styleName="three-col-twelve-container"
      ref={componentRef}
    >
      <div styleName="wrapper">
        {childCollections
          .slice(0, collectionCount)
          .map((childCollection, index) => getChildCollectionData(childCollection, config, index, qtConfig, pageType))}
        {!isSlotTypeStory && adWidgetComponent && <div styleName="ad-widget-container">{adWidgetComponent()}</div>}
      </div>
    </div>
  );
}

ThreeColTwelveStories.propTypes = {
  collection: PropTypes.object.isRequired,
  config: PropTypes.shape({
    borderColor: PropTypes.string,
    theme: PropTypes.string,
    border: PropTypes.string,
    slotConfig: PropTypes.array,
    footerButton: PropTypes.string,
    collectionNameTemplate: PropTypes.string,
    collectionNameBorderColor: PropTypes.string,
  }),
};

export default StateProvider(ThreeColTwelveStories);
